import { formatDate } from '@angular/common';
import { HttpClient, HttpEventType, HttpHeaders, HttpResponse } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { API_BASE_URL_SERVICE } from '../../../../kiene-core.config';
import { KieneBackendApiService } from '../../../../services/backend-api/kiene-backend-api.service';
import { LocalStorageService } from '../../../../services/local-storage/local-storage.service';
import { MessageService } from '../../../../services/message-service/message.service';
import { Gruppe } from '../../../gruppen/classes/gruppe';
import { KieneAutocompleteComponent } from '../../../kiene-autocomplete/kiene-autocomplete.component';
import { Haendler } from '../../../stammdaten/haendler/haendler';
import { Betriebskontakt } from '../../../stammdaten/betriebskontakte/betriebskontakt';
import { BetriebskontaktAuswahlConfig } from '../../../stammdaten/betriebskontakte/betriebskontakt-auswahl.component';
import { HaendlerDetailsDialogComponent } from '../../../stammdaten/haendler/haendler-details-dialog/haendler-details-dialog.component';
import { Upload } from '../gruppe-tier-pass-scan-verwaltung.component';
import { PassportJobLiveService } from '../passport-job-live.service';
import {
    GruppeTierScanImportierenDialogData,
    GruppeTierScanImportierenDialogPrefill,
    resolveGruppeTierScanImportierenDialogData,
} from './gruppe-tier-scan-importieren-dialog.data';

@Component({
    selector: 'kiene-gruppe-tier-scan-importieren-dialog',
    templateUrl: './gruppe-tier-scan-importieren-dialog.component.html',
    styleUrls: ['./gruppe-tier-scan-importieren-dialog.component.scss'],
})
export class GruppeTierScanImportierenDialogComponent implements OnInit, AfterViewInit, OnDestroy {
    gruppe: Gruppe;
    private prefill?: GruppeTierScanImportierenDialogPrefill;
    private prefillAbteil: string | null = null;
    private einstallTierlisteId: number | null = null;
    @ViewChild('fileSelector') fileSelector: ElementRef<HTMLInputElement>;
    @ViewChild('viehhaendlerAutoComplete')
    viehhaendlerAutoComplete: KieneAutocompleteComponent;
    pdfFile: File;
    uploadStatus: Upload;
    isLoading = false;
    uploadPercent = 0;
    uploadInFlight = false;
    private liveJobSub?: Subscription;
    liveStatusText = '';
    private closedWithRefresh = false;

    viehhaendlerCtrl: UntypedFormControl = new UntypedFormControl();
    kpEinstalldatumCtrl: UntypedFormControl = new UntypedFormControl();
    bemerkungCtrl: UntypedFormControl = new UntypedFormControl();
    scanProfilCtrl: UntypedFormControl = new UntypedFormControl('ear_tag_only');
    selectedBetriebskontakte: Betriebskontakt[] = [];
    betriebskontaktConfig: BetriebskontaktAuswahlConfig = {
        showLandwirte: true,
        showHaendler: true,
        showSchlachthoefe: true,
        showAll: true,
        multiSelect: false,
        compact: true,
        placeholder: 'Kontakt auswählen...',
        allowCreateNew: true,
    };
    readonly betriebskontaktCardConfig = {
        showActions: true,
        showMenu: false,
        showFavorit: false,
        showBadge: true,
        compact: false,
        selectedMode: true,
    };

    constructor(
        @Inject(API_BASE_URL_SERVICE) public apiBaseUrl,
        private api: KieneBackendApiService,
        public lss: LocalStorageService,
        private dialog: MatDialog,
        private messageService: MessageService,
        private http: HttpClient,
        private passportLive: PassportJobLiveService,
        public dialogRef: MatDialogRef<GruppeTierScanImportierenDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: Gruppe | GruppeTierScanImportierenDialogData,
    ) {
        const resolved = resolveGruppeTierScanImportierenDialogData(data);
        this.gruppe = resolved.gruppe;
        this.prefill = resolved.prefill;
        const tid = Number(resolved.einstall_tierliste_id || 0);
        this.einstallTierlisteId = tid > 0 ? tid : null;
    }

    ngOnInit(): void {
        this.applyPrefill();
    }

    ngAfterViewInit(): void {
        // Open file picker after optional prefill (incl. Händler autocomplete).
        setTimeout(() => this.openFileSelector(), 0);
    }

    private applyPrefill(): void {
        const prefill = this.prefill;
        if (!prefill) {
            return;
        }

        if (prefill.kpEinstalldatum) {
            this.kpEinstalldatumCtrl.setValue(prefill.kpEinstalldatum, { emitEvent: false });
        }
        if (prefill.bemerkung?.trim()) {
            this.bemerkungCtrl.setValue(prefill.bemerkung.trim(), { emitEvent: false });
        }
        const abteil = prefill.abteil?.trim();
        if (abteil) {
            this.prefillAbteil = abteil;
        }
        if (prefill.betriebskontakt) {
            this.onBetriebskontaktSelected(prefill.betriebskontakt);
        } else if (prefill.viehhaendler?.viehhaendler_id) {
            this.onViehhaendlerSelected(prefill.viehhaendler);
            setTimeout(() => this.viehhaendlerAutoComplete?.setValue(prefill.viehhaendler, true), 0);
        }
    }

    openFileSelector(): void {
        if (this.isLoading) {
            return;
        }
        this.fileSelector?.nativeElement?.click();
    }

    ngOnDestroy(): void {
        this.cleanupLiveMode();
    }

    handleFiles(event: any) {
        if (event) {
            const files = event.target.files;
            this.pdfFile = files.item(0);
            this.uploadPercent = 0;
            this.liveStatusText = '';
            this.uploadStatus = undefined;
        }
    }

    progressScan() {
        if (this.uploadInFlight || !this.isValid()) {
            return;
        }
        this.uploadInFlight = true;
        this.isLoading = true;
        this.uploadPercent = 0;
        this.uploadStatus = { state: 'IN_PROGRESS', progress: 0 };
        this.liveStatusText = 'Upload wird vorbereitet…';

        const fd = this.buildUploadFormData();
        this.http
            .post<{ job_id?: number; jobId?: number; grps_id?: number }>(
                `${this.apiBaseUrl}rinderpassscan/create.php`,
                fd,
                {
                    headers: new HttpHeaders('Phito-Type: fileupload'),
                    reportProgress: true,
                    observe: 'events',
                },
            )
            .subscribe({
                next: event => {
                    if (event.type === HttpEventType.UploadProgress) {
                        const total = event.total ?? this.pdfFile?.size ?? 0;
                        if (total > 0) {
                            const raw = Math.round((85 * event.loaded) / total);
                            this.setUploadProgress(raw, 'Upload');
                        } else if (event.loaded > 0) {
                            this.setUploadProgress(10, 'Upload');
                        }
                        return;
                    }
                    if (event instanceof HttpResponse) {
                        this.setUploadProgress(95, 'OCR-Job wird eingereiht');
                        const response = event.body;
                        const jobId = Number(response?.job_id ?? response?.jobId ?? 0);
                        this.setUploadProgress(100, 'Fertig');
                        if (jobId > 0) {
                            this.messageService.infoMessage(
                                `Upload erfolgreich (Job ${jobId}). Die Verarbeitung läuft im Hintergrund.`,
                            );
                        } else {
                            this.messageService.infoMessage(
                                'Upload erfolgreich. Die Datei wird im Hintergrund verarbeitet.',
                            );
                        }
                        this.cleanupLiveMode();
                        this.closeAndRefreshScanList();
                    }
                },
                error: err => {
                    this.uploadInFlight = false;
                    this.isLoading = false;
                    this.uploadStatus = undefined;
                    this.liveStatusText = '';
                    this.messageService.errorMessage('Fehler beim Hochladen der Datei!', err);
                    this.cleanupLiveMode();
                },
            });
    }

    private setUploadProgress(percent: number, phase: 'Upload' | 'OCR-Job wird eingereiht' | 'Fertig'): void {
        this.uploadPercent = Math.min(100, Math.max(0, percent));
        this.uploadStatus = { state: 'IN_PROGRESS', progress: this.uploadPercent };
        if (phase === 'Fertig') {
            this.liveStatusText = 'Upload abgeschlossen';
        } else {
            this.liveStatusText = `${phase} … ${this.uploadPercent} %`;
        }
    }

    private buildUploadFormData(): FormData {
        const fd = new FormData();
        fd.append('dokument', this.pdfFile);
        if (this.selectedBetriebskontakte?.length > 0) {
            const kontakt = this.selectedBetriebskontakte[0];
            if (kontakt?.kunde_id) {
                fd.append('betriebskontakt_haendler_id', kontakt.kunde_id.toString());
            }
        } else {
            const viehhaendler = this.viehhaendlerCtrl.value as Haendler;
            if (viehhaendler?.viehhaendler_id) {
                fd.append('viehhaendler_id', viehhaendler.viehhaendler_id.toString());
            }
        }
        fd.append('kp_einstalldatum', this.formatKpEinstalldatum());
        fd.append('gruppe_id', this.gruppe.gruppe_id.toString());
        if (this.einstallTierlisteId && this.einstallTierlisteId > 0) {
            fd.append('einstall_tierliste_id', String(this.einstallTierlisteId));
        }
        if (this.bemerkungCtrl?.value) {
            fd.append('einstall_bemerkung', this.bemerkungCtrl.value.toString());
        }
        if (this.prefillAbteil) {
            fd.append('abteil', this.prefillAbteil);
        }
        const scanProfil = this.scanProfilCtrl.value === 'full' ? 'full' : 'ear_tag_only';
        fd.append('scan_profil', scanProfil);
        return fd;
    }

    private formatKpEinstalldatum(): string {
        const kpDate = this.kpEinstalldatumCtrl.value ? new Date(this.kpEinstalldatumCtrl.value) : null;
        if (!kpDate || Number.isNaN(kpDate.getTime())) {
            return '';
        }
        return formatDate(kpDate, 'yyyy-MM-dd', 'de', 'Europe/Berlin');
    }

    private closeAndRefreshScanList(): void {
        if (this.closedWithRefresh) {
            return;
        }
        this.closedWithRefresh = true;
        this.dialogRef.close(true);
    }

    private cleanupLiveMode() {
        this.liveJobSub?.unsubscribe();
        this.liveJobSub = undefined;
        this.passportLive.disconnect();
        this.isLoading = false;
        this.uploadInFlight = false;
    }

    isValid() {
        return this.kpEinstalldatumCtrl.value && this.pdfFile;
    }

    onViehhaendlerSelected(haendler: Haendler | null) {
        this.selectedBetriebskontakte = [];
        this.viehhaendlerCtrl.setValue(haendler, { emitEvent: false });
    }

    onBetriebskontaktSelected(kontakt: Betriebskontakt) {
        if (kontakt) {
            this.selectedBetriebskontakte = [kontakt];
            this.viehhaendlerCtrl.setValue(null, { emitEvent: false });
        } else {
            this.selectedBetriebskontakte = [];
        }
    }

    onBetriebskontakteSelected(kontakte: Betriebskontakt[]) {
        if (kontakte?.length > 0) {
            this.onBetriebskontaktSelected(kontakte[0]);
        } else {
            this.selectedBetriebskontakte = [];
        }
    }

    onBetriebskontaktEntfernen() {
        this.selectedBetriebskontakte = [];
    }

    addViehhaendler() {
        this.dialog
            .open(HaendlerDetailsDialogComponent, { minWidth: '50vw' })
            .afterClosed()
            .subscribe((res: Haendler) => {
                if (res) {
                    this.onViehhaendlerSelected(res);
                    this.viehhaendlerAutoComplete.setValue(res, true);
                }
            });
    }
}
